/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IPopup } from 'app/shared/elements/popup-base';
import { AuthMedicationsGrid, AuthMedicationEvent, IAuthMedicationsGridOptions, defaultOptions as defaultAuthMedicationsGridOptions } from 'app/um/components/auth/medications/auth-medications-grid';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { getReference } from 'app/shared/models/essette-types';
import { AuthService, IAuthMedicationStatusSearchFilters } from 'app/um/services/auth-service';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { fireEvents } from 'app/shared/lib/fire-events';
import { DrugSearch, DrugSelectEvent } from 'app/cd/components/drugs/drug-search';
import { deepClone } from 'app/shared/lib/utilities';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { AuthReloadEvent } from 'app/um/lib/events';
import { validate } from 'app/shared/elements/ess-rule';
import { ISaveAuthMedicationOptions, SaveAuthMedication, defaultOptions as defaultSaveAuthMedicationOptions } from 'app/um/components/auth/medications/save-auth-medication';
import { IMedicationChange } from 'app/pp/components/update/update-auth-details-step';
import { IAuthContextSettingsDetails } from 'app/um/lib/auth-context';
import { guid } from 'app/shared/lib/guid';
import { I18N } from 'aurelia-i18n';
import { ActionOptions } from 'app/shared/lib/api/api-types';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class AuthMedications extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    public auth: UM.Auth | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;

    @bindable.object()
    public drugApprovedCharges: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugRequestedCharges: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugRequestedDays: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugRequestedQuantity: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugsAuthDate: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugsExpDate: IAuthContextSettingsDetails = {};

    @bindable.objectNull()
    public drugsStatusReason: IAuthContextSettingsDetails | null = null;

    @bindable.boolean()
    public showWarnings: boolean = true;

    @bindable.boolean()
    public hideBulkApplyDates: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public authMedicationsGridOptions: IAuthMedicationsGridOptions = defaultAuthMedicationsGridOptions;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public saveAuthMedicationOptions: ISaveAuthMedicationOptions = defaultSaveAuthMedicationOptions;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected pendingMedicationChanges: IMedicationChange[] = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    public insertedAuthMedicationIds: string[] = [];

    private medications: ReadonlyArray<CD.Drug> = [];
    protected authId: string | null = null;
    protected editMedication: UM.AuthMedication | null = null;
    protected statuses: UM.AuthMedicationStatus[] = [];
    protected loadModal: boolean = false;
    protected drugSearchQuery: string | null = null;
    protected includeInactive = false;

    protected addedFromPortalEditMode: boolean = false;

    private authReloaded: Subscription | null = null;
    protected $isValid: boolean = true;
    protected validate = validate;
    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $searchModal!: IPopup;
    protected $editModal!: IPopup;
    protected $editMedication!: SaveAuthMedication;
    protected $drugSearch!: DrugSearch;
    protected $grid!: AuthMedicationsGrid;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly authService: AuthService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.auth) {
            throw new Error('No auth information was bound');
        }

        if(!this.readonly) this.loadLookups();

        this.authId = this.auth.id || null;
        this.drugSearchQuery = null;
        this.includeInactive = false;

        //TODO JW Do we need this anymore? Its bound now
        this.authReloaded = this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => {
            this.authId = this.auth?.id || null;
        });
    }

    protected detached(): void {
        if(this.authReloaded) {
            this.authReloaded.dispose();
        }
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    protected searchDrugs(generalSearch: string): void {
        this.$searchModal.open();
        this.$drugSearch.search({ general: generalSearch })
    }

    protected closeDrugSearch(): void {
        this.drugSearchQuery = null;
        this.$drugSearch.reset();
        this.$searchModal.close();
    }

    protected closeEdit(): void {
        this.$editMedication.reset();
        this.$editModal.close();
    }

    protected drugSelected(event: DrugSelectEvent) {
        if(event && event.detail && event.detail.drug) {
            const selectedDrug = event.detail.drug;
            const authMedication: UM.AuthMedication = {
                auth: getReference(this.auth),
                definition: getReference(selectedDrug)
            }

            if(this.authMedicationsGridOptions.pendMedicationChanges) {
                if(!this.pendingMedicationChanges) this.pendingMedicationChanges = [];
                authMedication.definition = {
                    id: selectedDrug.id,
                    code: selectedDrug.code,
                    description: selectedDrug.description,
                    type: {
                        code: selectedDrug.type?.code
                    },
                    brandName: selectedDrug.brandName,
                    genericName: selectedDrug.genericName,
                    labelInformation: selectedDrug.labelInformation,
                    category: {
                        description: selectedDrug.category?.description
                    }
                }
                this.pendingMedicationChanges.push({ medication: authMedication, type: ActionOptions.Insert });
                const lowercaseMedication = this.i18n.tr('um:medication.$').toLowerCase();
                this.toaster.info().message(this.i18n.tr('ui:messages.events.addToPending', { what: lowercaseMedication })).show();
            }
            else {
                this.addAuthMedication(authMedication);
            }
            this.drugSearchQuery = null;
            this.$searchModal.close();
        }
    }

    //TODO: [Nick] This doesn't appear to be being used?
    private drugExists(drug: CD.Drug): boolean {
        if(!(this.medications && this.medications.length)) {
            return false;
        }

        return this.medications.findIndex(m => m.id === drug.id) >= 0;
    }

    protected editDrug(event: AuthMedicationEvent): void {
        if(!event.detail || !event.detail.medication) {
            this.logger.error('Cannot edit medication. Object is null or undefined');
            return;
        }

        const { medication } = event.detail;

        this.editMedication = deepClone(medication);
        this.addedFromPortalEditMode = !!this.saveAuthMedicationOptions.portalEditMode && !!this.editMedication.id && this.insertedAuthMedicationIds.includes(this.editMedication.id);
        this.loadModal = true;
        this.$editModal.open();
    }

    public async reload(): Promise<void> {
        if(!this.$grid) return;
        try {
            this.state.beginLoad();
            this.authId = this.auth?.id || null;
            this.drugSearchQuery = null;
            this.$grid.includeInactive = this.includeInactive;
            this.$grid.reload();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    protected async formSuccess(e: FormSuccessCustomEvent): Promise<void> {
        if(!this.$editModal) return;
        this.$editModal.close();
        await this.reload();
        if(this.authMedicationsGridOptions.pendMedicationChanges) {
            const lowercaseMedication = this.i18n.tr('um:medication.$').toLowerCase();
            this.toaster.info().message(this.i18n.tr('ui:messages.events.addToPending', { what: lowercaseMedication })).show();
            fireEvents(this.$element, ['auth-change'], { authReloaded: false, triggerRules: false });
            return;
        }
        fireEvents(this.$element, ['auth-change'], { authReloaded: false, triggerRules: false });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async addAuthMedication(authMedication: UM.AuthMedication): Promise<void> {
        if(!(this.auth && authMedication)) {
            throw new Error('Unable to add medication: Missing [auth] or [drug]');
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.insertAuthMedication(authMedication);
            isSuccessful = response.isSuccessful;
            await this.reload();
            fireEvents(this.$element, ['auth-change'], { authReloaded: false, triggerRules: true });
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.added('um:medication.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();
            const includes = [
                'determination.status',
                'reasons'
            ];
            const fields = [
                'code',
                'description',
                'determination.status.determination.result',
                'reasons.code',
                'reasons.description'
            ];
            const filters: IAuthMedicationStatusSearchFilters = { withReasons: !!this.drugsStatusReason?.visible || this.authMedicationsGridOptions?.showStatusReason };

            const response = await this.authService.loadAuthMedicationStatuses({ all: true }, includes, filters, fields);
            this.statuses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
